import type { TimelineActual, TimelineStop, TripTimeline } from '../trip-timeline.api';
import { referenceTime } from './timeline-reference.policy';
import { clockWithDay } from './timeline-time.policy';
import { varianceLabel } from './timeline-variance-label.policy';

interface SummaryInput {
  tripNumber: string;
  timeline: TripTimeline;
  /** The company's time zone: the summary is pasted to a customer, who reads the clock where the company is. */
  timezone: string;
}

const PHRASES: Record<string, (stopName: string, time: string) => string> = {
  arrived: (name, time) => `Arrived at ${name} ${time}`,
  loaded: (name, time) => `Loaded at ${name} ${time}`,
  unloaded: (name, time) => `Offloaded at ${name} ${time}`,
  departed: (name, time) => `Left ${name} ${time}`,
  empty_returned: (name, time) => `Empty returned at ${name} ${time}`,
};

/** The yard is where the trip starts from: nothing is reported about it, and a customer is not told about it. */
const isReportable = (stop: TimelineStop) => stop.kind !== 'yard';

function actualLine(stop: TimelineStop, actual: TimelineActual, clock: (iso: string) => string): string | null {
  const phrase = PHRASES[actual.milestone];
  if (!phrase) return null;
  const line = phrase(stop.name, clock(actual.at));
  const planned = stop.planned.arrivalAt;
  if (actual.milestone !== 'arrived' || !planned) return `${line}.`;
  const label = varianceLabel(stop.variance.arrivalSeconds);
  return `${line}, planned ${clock(planned)}${label ? ` (${label.text})` : ''}.`;
}

function nextStopLine(stops: TimelineStop[], clock: (iso: string) => string): string | null {
  // A depot the empty was returned at counts as reached even with no "arrived" tap, which nobody makes there.
  const next = stops.find((stop) => stop.actuals.length === 0);
  if (!next) return null;
  return next.planned.arrivalAt ? `Next: ${next.name}, planned ${clock(next.planned.arrivalAt)}.` : `Next: ${next.name}.`;
}

/**
 * Plain text a dispatcher can paste into a message to a customer about where the trip is (gitaumoses4/chukua#90; V1 notifies by hand).
 * One line per fact in the order it happened, with the lateness in words, and what comes next on the plan. Worked out on the web from the
 * timeline the API sent, so the API needs no time zone and the text can never say more than the timeline shows.
 */
export function statusSummary({ tripNumber, timeline, timezone }: SummaryInput): string {
  const reference = referenceTime(timeline);
  const clock = (iso: string) => (reference ? clockWithDay(iso, reference, timezone) : iso);
  const stops = timeline.stops.filter(isReportable);
  const destination = [...stops].reverse().find((stop) => stop.kind === 'drop');

  const facts = stops.flatMap((stop) => stop.actuals.flatMap((actual) => actualLine(stop, actual, clock) ?? []));
  return [
    `Trip ${tripNumber}${destination ? ` to ${destination.name}` : ''}.`,
    ...(facts.length > 0 ? facts : ['No stops reported yet.']),
    nextStopLine(stops, clock),
  ]
    .filter((line): line is string => line !== null)
    .join('\n');
}
